/**
 * Liste des feuilles journalières en attente de validation (pour RH)
 * Affiche toutes les feuilles soumises avec possibilité de valider/rejeter
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import { useNotifications } from '../../contexts/NotificationContext';
import { getJWTPayload } from '../../utils/authUtils';
import { toLocalDateStr } from '../../utils/dateUtils';
import { PageHeader } from '../common';
import DailyWorksheet from './DailyWorksheet';
import './WorksheetValidationList.css';

const STATUS_INFO = {
  draft: { label: 'Brouillon', color: '#9E9E9E' },
  submitted: { label: 'En attente', color: '#FF9800' },
  validated: { label: 'Validé', color: '#4CAF50' },
  checked: { label: 'Contrôlée', color: '#1976D2' },
  rejected: { label: 'Rejeté', color: '#F44336' }
};

const WorksheetValidationList = ({ token }) => {
  const { showSuccess, showError, showWarning } = useNotifications();

  // Vérifier si l'utilisateur peut valider (permissions backend: work-sheets.validate, users.manage, availabilities.manage)
  const isValidator = useMemo(() => {
    const payload = getJWTPayload(token);
    const permissions = payload?.permissions || [];
    return permissions.includes('all') ||
      permissions.includes('work-sheets.validate') ||
      permissions.includes('availabilities.manage') ||
      permissions.includes('users.manage');
  }, [token]);

  // Seuls les rôles Comptabilité / Admin peuvent contrôler une feuille validée
  const isAccountant = useMemo(() => {
    const payload = getJWTPayload(token);
    return payload?.role === 'Comptabilité' || payload?.role === 'Admin';
  }, [token]);

  const [loading, setLoading] = useState(true);
  const [worksheets, setWorksheets] = useState([]);
  const [filter, setFilter] = useState('submitted'); // Par défaut : en attente
  const [searchFilters, setSearchFilters] = useState({
    start_date: '',
    end_date: '',
    order_number: '',
    client_name: '',
    group_name: ''
  });
  const [selectedWorksheet, setSelectedWorksheet] = useState(null);
  const [stats, setStats] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null); // { url, filename }
  const [loadingPdf, setLoadingPdf] = useState(null); // worksheetId en cours de chargement

  // Charger les statistiques
  const loadStats = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.WORKSHEET_STATS_PENDING, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  }, [token]);

  // Charger les feuilles
  const loadWorksheets = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filter && filter !== 'all') {
        params.set('status', filter);
      }
      if (searchFilters.start_date) params.set('start_date', searchFilters.start_date);
      if (searchFilters.end_date) params.set('end_date', searchFilters.end_date);
      if (searchFilters.order_number) params.set('order_number', searchFilters.order_number);
      if (searchFilters.client_name) params.set('client_name', searchFilters.client_name);
      if (searchFilters.group_name) params.set('group_id', searchFilters.group_name);

      const qs = params.toString();
      const url = API_ENDPOINTS.WORKSHEETS + (qs ? `?${qs}` : '');

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setWorksheets(data.data || []);
      }
    } catch (error) {
      console.error('Erreur chargement worksheets:', error);
      showError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token, filter, searchFilters, showError]);

  useEffect(() => {
    loadStats();
    loadWorksheets();
  }, [loadStats, loadWorksheets]);

  // Valider une feuille
  const handleValidate = async (worksheetId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await fetch(API_ENDPOINTS.WORKSHEET_VALIDATE(worksheetId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: user.id })
      });

      const data = await response.json();

      if (data.success) {
        showSuccess('Feuille validée');
        loadWorksheets();
        loadStats();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      showError(error.message || 'Erreur de validation');
    }
  };

  // Rejeter une feuille
  const handleReject = async (worksheetId) => {
    if (!rejectReason.trim()) {
      showWarning('Veuillez indiquer une raison');
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const response = await fetch(API_ENDPOINTS.WORKSHEET_REJECT(worksheetId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: user.id,
          reason: rejectReason
        })
      });

      const data = await response.json();

      if (data.success) {
        showSuccess('Feuille rejetée');
        setShowRejectModal(null);
        setRejectReason('');
        loadWorksheets();
        loadStats();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      showError(error.message || 'Erreur de rejet');
    }
  };

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Normaliser une date en format YYYY-MM-DD
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    // Si c'est déjà au format YYYY-MM-DD, le retourner tel quel
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // Sinon, parser et formater
    const date = new Date(dateStr);
    return toLocalDateStr(date);
  };

  // Ouvrir une feuille pour consultation
  const handleOpenWorksheet = (worksheet) => {
    setSelectedWorksheet(worksheet);
  };

  // Générer et afficher le PDF
  const handleExportPdf = async (worksheet) => {
    try {
      setLoadingPdf(worksheet.id);

      const response = await fetch(API_ENDPOINTS.WORKSHEET_PDF(worksheet.id), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // Essayer de lire le message d'erreur du backend
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Erreur lors de la génération du PDF');
        }
        throw new Error('Erreur lors de la génération du PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const dateStr = formatDate(worksheet.work_date).replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `feuille_${worksheet.group_name || 'groupe'}_${dateStr}.pdf`;

      setPdfPreview({ url, filename });
    } catch (error) {
      showError(error.message || 'Erreur de génération PDF');
    } finally {
      setLoadingPdf(null);
    }
  };

  // Fermer la visionneuse PDF
  const closePdfPreview = () => {
    if (pdfPreview?.url) {
      window.URL.revokeObjectURL(pdfPreview.url);
    }
    setPdfPreview(null);
  };

  // Télécharger le PDF depuis la visionneuse
  const downloadPdf = () => {
    if (!pdfPreview) return;
    const link = document.createElement('a');
    link.href = pdfPreview.url;
    link.download = pdfPreview.filename;
    link.click();
  };

  // Imprimer le PDF
  const printPdf = () => {
    if (!pdfPreview) return;
    const printWindow = window.open(pdfPreview.url);
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  if (selectedWorksheet) {
    const normalizedDate = normalizeDate(selectedWorksheet.work_date);
    return (
      <div className="worksheet-validation-fullscreen">
        <DailyWorksheet
          prestationId={selectedWorksheet.prestation_id}
          workDate={normalizedDate}
          onClose={() => {
            setSelectedWorksheet(null);
            loadWorksheets();
            loadStats();
          }}
          readOnly={['validated', 'checked'].includes(selectedWorksheet.status_code)}
        />
      </div>
    );
  }

  return (
    <div className="worksheet-validation-list">
      <PageHeader
        icon="📝"
        title="Validation des Feuilles"
        color="green"
        actions={
          stats && (
            <div className="stats-badges">
              <span className="stat-badge pending" onClick={() => setFilter('submitted')}>
                {stats.pending_count} en attente
              </span>
              <span className="stat-badge validated" onClick={() => setFilter('validated')}>
                {stats.validated_count} validées
              </span>
              <span className="stat-badge checked" onClick={() => setFilter('checked')}>
                {stats.checked_count || 0} contrôlées
              </span>
              <span className="stat-badge rejected" onClick={() => setFilter('rejected')}>
                {stats.rejected_count} rejetées
              </span>
            </div>
          )
        }
      />

      <div className="validation-filters">
        <label>Statut :</label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">Tous</option>
          <option value="submitted">En attente de validation</option>
          <option value="validated">Validées</option>
          <option value="checked">Contrôlées</option>
          <option value="rejected">Rejetées</option>
          <option value="draft">Brouillons</option>
        </select>

        <label>Du :</label>
        <input
          type="date"
          value={searchFilters.start_date}
          onChange={(e) => setSearchFilters(f => ({ ...f, start_date: e.target.value }))}
          className="filter-input filter-date"
        />
        <label>Au :</label>
        <input
          type="date"
          value={searchFilters.end_date}
          onChange={(e) => setSearchFilters(f => ({ ...f, end_date: e.target.value }))}
          className="filter-input filter-date"
        />

        <input
          type="text"
          placeholder="N° commande"
          value={searchFilters.order_number}
          onChange={(e) => setSearchFilters(f => ({ ...f, order_number: e.target.value }))}
          className="filter-input"
        />
        <input
          type="text"
          placeholder="Client"
          value={searchFilters.client_name}
          onChange={(e) => setSearchFilters(f => ({ ...f, client_name: e.target.value }))}
          className="filter-input"
        />
        <input
          type="text"
          placeholder="Groupe"
          value={searchFilters.group_name}
          onChange={(e) => setSearchFilters(f => ({ ...f, group_name: e.target.value }))}
          className="filter-input"
        />

        {(searchFilters.start_date || searchFilters.end_date || searchFilters.order_number || searchFilters.client_name || searchFilters.group_name) && (
          <button
            className="btn-clear-filters"
            onClick={() => setSearchFilters({ start_date: '', end_date: '', order_number: '', client_name: '', group_name: '' })}
            title="Effacer les filtres"
          >
            Effacer
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : worksheets.length === 0 ? (
        <div className="empty-state">
          <p>Aucune feuille journalière {filter === 'submitted' ? 'en attente de validation' : 'trouvée'}</p>
        </div>
      ) : (
        <div className="worksheets-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>N° Commande</th>
                <th>Groupe</th>
                <th>Client</th>
                <th>Lieu</th>
                <th>Chef d'équipe</th>
                <th>Participants</th>
                <th>Soumis par</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {worksheets.map(ws => (
                <tr key={ws.id} className={`status-${ws.status_code}`}>
                  <td className="date-cell">{formatDate(ws.work_date)}</td>
                  <td className="prestation-cell">{ws.order_number || '-'}</td>
                  <td>{ws.group_name || '-'}</td>
                  <td>{ws.client_name || '-'}</td>
                  <td>{ws.location_name || '-'}</td>
                  <td>{ws.team_leader_name || '-'}</td>
                  <td className="count-cell">{ws.entry_count}</td>
                  <td>{ws.submitted_by_name || '-'}</td>
                  <td>
                    <span
                      className="status-badge"
                      style={{ backgroundColor: STATUS_INFO[ws.status_code]?.color }}
                    >
                      {STATUS_INFO[ws.status_code]?.label}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button
                      className="btn-view"
                      onClick={() => handleOpenWorksheet(ws)}
                      title="Voir la feuille"
                    >
                      👁️
                    </button>

                    <button
                      className="btn-pdf"
                      onClick={() => handleExportPdf(ws)}
                      title="Exporter en PDF"
                      disabled={loadingPdf === ws.id}
                    >
                      {loadingPdf === ws.id ? '⏳' : '📄'}
                    </button>

                    {ws.status_code === 'submitted' && isValidator && (
                      <>
                        <button
                          className="btn-validate"
                          onClick={() => handleValidate(ws.id)}
                          title="Valider"
                        >
                          ✅
                        </button>
                        <button
                          className="btn-reject"
                          onClick={() => setShowRejectModal(ws.id)}
                          title="Rejeter"
                        >
                          ❌
                        </button>
                      </>
                    )}

                    {ws.status_code === 'validated' && isAccountant && (
                      <button
                        className="btn-check"
                        onClick={() => handleOpenWorksheet(ws)}
                        title="Ouvrir pour contrôler (comptabilité)"
                      >
                        🔒
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de rejet */}
      {showRejectModal && (
        <div className="reject-modal-overlay">
          <div className="reject-modal">
            <h3>Rejeter la feuille</h3>
            <p>Veuillez indiquer la raison du rejet :</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Raison du rejet..."
              rows={4}
            />
            <div className="reject-modal-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectReason('');
                }}
              >
                Annuler
              </button>
              <button
                className="btn-confirm-reject"
                onClick={() => handleReject(showRejectModal)}
              >
                Rejeter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visionneuse PDF */}
      {pdfPreview && (
        <div className="pdf-preview-overlay" onClick={closePdfPreview}>
          <div className="pdf-preview-header" onClick={(e) => e.stopPropagation()}>
            <h3>{pdfPreview.filename}</h3>
            <div className="pdf-preview-actions">
              <button className="btn-print" onClick={printPdf} title="Imprimer">
                🖨️ Imprimer
              </button>
              <button className="btn-download" onClick={downloadPdf} title="Télécharger">
                ⬇️ Télécharger
              </button>
              <button className="btn-close" onClick={closePdfPreview}>
                ✕
              </button>
            </div>
          </div>
          <div className="pdf-preview-content" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={pdfPreview.url}
              title="Aperçu PDF"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WorksheetValidationList;
